import * as THREE from 'three';

export function createPlayerCombatFluidSystem({
  camera,
  world,
  player,
  ui,
  itemDefs,
  mobSystem,
  heldItem,
  sounds,
  particles,
  blockInteraction,
  isCreativeMode,
  getRangedCooldown,
  setRangedCooldown,
  getBowChargeState
} = {}) {
  const attackOrigin = new THREE.Vector3();
  const attackDir = new THREE.Vector3();

  // screen shake state
  let shakeIntensity = 0;
  let shakeDecay = 12;
  let shakeOffsetX = 0;
  let shakeOffsetZ = 0;

  // hitstop state
  let hitStopTimer = 0;

  // attack vignette
  const attackVignetteEl = document.getElementById('attack-vignette');
  let vignetteIntensity = 0;

  // FOV zoom punch
  const baseFov = camera?.fov || 75;
  let fovPunch = 0;
  let fovPunchDecay = 0;

  function getPlayerAttackDamage() {
    const itemId = ui.getActiveItemId();
    if (!itemId || ui.getActiveItemCount() <= 0) return 1;

    const def = itemDefs[itemId];
    if (!def) return 1;
    if (def.kind === 'tool') return def.attackDamage || 1.0;
    if (def.kind === 'block') return 1.2;
    return 1.0;
  }

  function swapActiveItemOne(fromId, toId) {
    if (ui.getActiveItemId() !== fromId || ui.getActiveItemCount() <= 0) return false;
    if (!ui.consumeActiveItem(1)) return false;
    if (ui.addItem(toId, 1)) {
      ui.assignItemToHotbar(toId);
      return true;
    }
    ui.addItem(fromId, 1);
    return false;
  }

  function getPlacementCandidateFromHit(hit) {
    if (!hit?.object || !hit?.face) return null;
    const place = hit.object.position.clone().add(hit.face.normal);
    return {
      x: Math.round(place.x),
      y: Math.round(place.y),
      z: Math.round(place.z)
    };
  }

  function canPlaceFluidAtCandidate(candidate) {
    if (!candidate) return false;
    if (!world.canPlaceAt(candidate.x, candidate.y, candidate.z)) return false;
    return !player.blockIntersectsPlayer(candidate.x, candidate.y, candidate.z, player.getPosition(), player.getHeight());
  }

  function findFluidTargetInView(maxDist = 6) {
    camera.getWorldPosition(attackOrigin);
    camera.getWorldDirection(attackDir);

    const sampled = new Set();
    const step = 0.08;
    for (let t = 0; t <= maxDist; t += step) {
      const px = attackOrigin.x + (attackDir.x * t);
      const py = attackOrigin.y + (attackDir.y * t);
      const pz = attackOrigin.z + (attackDir.z * t);
      const bx = Math.floor(px + 0.5);
      const by = Math.floor(py + 0.5);
      const bz = Math.floor(pz + 0.5);
      const key = `${bx},${by},${bz}`;
      if (sampled.has(key)) continue;
      sampled.add(key);

      const id = world.getBlockIdAt(bx, by, bz);
      if (id === 7 || id === 69) {
        const fluidBlock = world.getBlockAt(bx, by, bz);
        if (fluidBlock) return fluidBlock;
      }

      if (id !== null && world.isSolidAt(bx, by, bz)) break;
    }

    return null;
  }

  function tryAttackMob(blockHit = null) {
    camera.getWorldPosition(attackOrigin);
    camera.getWorldDirection(attackDir);

    const attackDamage = getPlayerAttackDamage();
    const maxRange = Math.min(3.25, blockHit ? Math.max(0, blockHit.distance - 0.03) : 3.25);
    if (maxRange <= 0.05) return false;

    const result = mobSystem.tryPlayerAttack(attackOrigin, attackDir, attackDamage, maxRange);
    if (!result.hit) return false;

    // impact sound
    if (sounds.mobHit) sounds.mobHit();
    else sounds.hitByBlockId(5);

    // hit particles at impact point
    if (particles?.spawnMobHitBurst && result.hitPoint) {
      particles.spawnMobHitBurst(result.hitPoint.x, result.hitPoint.y, result.hitPoint.z);
    }

    // screen shake – strong, punchy
    shakeIntensity = 0.025;
    shakeDecay = 12;

    // hitstop (brief freeze)
    hitStopTimer = 0.04;

    // attack vignette flash
    vignetteIntensity = Math.min(1, vignetteIntensity + 0.6);

    const activeItemId = ui.getActiveItemId();
    const def = itemDefs[activeItemId];
    if (!isCreativeMode() && def && def.kind === 'tool') ui.damageActiveTool(1);

    return true;
  }

  function tryShootBow() {
    const activeItemId = ui.getActiveItemId();
    const creative = isCreativeMode();
    if (activeItemId !== 'bow' || ui.getActiveItemCount() <= 0) return false;
    if ((getRangedCooldown() || 0) > 0) return false;
    if (!creative && !ui.hasItem('arrow', 1)) return false;

    camera.getWorldPosition(attackOrigin);
    camera.getWorldDirection(attackDir);
    const bowDef = itemDefs[activeItemId];
    const bowState = getBowChargeState?.() || {};
    const charging = bowState.charging === true;
    const startTime = Number.isFinite(bowState.startTime) ? bowState.startTime : 0;

    const charge = charging
      ? THREE.MathUtils.clamp((performance.now() - startTime) / 950, 0.12, 1.0)
      : 0.82;
    const spawnPos = attackOrigin.clone().addScaledVector(attackDir, 0.72);
    spawnPos.y -= 0.12;
    const speed = THREE.MathUtils.lerp(10.8, 23.5, charge);
    const damage = (bowDef?.rangedDamage || 8.0) * THREE.MathUtils.lerp(0.45, 1.1, charge);
    if (!mobSystem.spawnPlayerArrow(spawnPos, attackDir, speed, damage)) return false;

    if (!creative) {
      if (!ui.removeItem('arrow', 1)) return false;
      ui.damageActiveTool(1);
    }
    if (sounds.bowShoot) sounds.bowShoot();
    heldItem.triggerSwing(THREE.MathUtils.lerp(0.34, 1.0, charge));
    setRangedCooldown(THREE.MathUtils.lerp(0.7, 0.35, charge));
    return true;
  }

  function tryScoopFluidWithBucket(hit) {
    if (ui.getActiveItemId() !== 'bucket' || ui.getActiveItemCount() <= 0) return false;
    let target = hit?.object || null;
    if (target?.userData?.id !== 7 && target?.userData?.id !== 69) {
      target = findFluidTargetInView(6);
    }
    if (!target) return false;

    const fluidId = target.userData?.id;
    if (fluidId !== 7 && fluidId !== 69) return false;
    if (fluidId === 7 && target.userData?.waterSource !== true) return false;
    if (fluidId === 69 && target.userData?.lavaSource !== true) return false;

    const x = Math.round(target.position.x);
    const y = Math.round(target.position.y);
    const z = Math.round(target.position.z);
    const fillItemId = fluidId === 7 ? 'water_bucket' : 'lava_bucket';

    world.removeBlock(target, { trackModification: true });
    if (!swapActiveItemOne('bucket', fillItemId)) {
      world.createBlock(x, y, z, fluidId);
      return false;
    }

    heldItem.triggerSwing(0.72);
    sounds.pickup();
    return true;
  }

  function tryPourFluidFromBucket(hit, activeItemId) {
    if (activeItemId !== 'water_bucket' && activeItemId !== 'lava_bucket') return false;
    if (ui.getActiveItemCount() <= 0) return false;

    const fluidId = activeItemId === 'water_bucket' ? 7 : 69;
    const candidate = getPlacementCandidateFromHit(hit);
    if (!canPlaceFluidAtCandidate(candidate)) return false;

    const placed = world.createBlock(candidate.x, candidate.y, candidate.z, fluidId);
    if (!placed) return false;
    if (!isCreativeMode() && !swapActiveItemOne(activeItemId, 'bucket')) {
      world.removeBlock(placed, { trackModification: true });
      return false;
    }

    heldItem.triggerSwing(0.72);
    sounds.placeByBlockId(fluidId);
    world.settleGravityAround(candidate.x, candidate.y, candidate.z, 0);
    blockInteraction.refreshPlacementPreview(true);
    return true;
  }

  function updateShake(delta) {
    if (hitStopTimer > 0) hitStopTimer = Math.max(0, hitStopTimer - delta);

    // --- camera shake ---
    if (shakeIntensity > 0.0001) {
      camera.rotation.x -= shakeOffsetX;
      camera.rotation.z -= shakeOffsetZ;
      shakeOffsetX = (Math.random() * 2 - 1) * shakeIntensity;
      shakeOffsetZ = (Math.random() * 2 - 1) * shakeIntensity * 0.7;
      camera.rotation.x += shakeOffsetX;
      camera.rotation.z += shakeOffsetZ;
      shakeIntensity *= Math.max(0, 1 - shakeDecay * delta);
      if (shakeIntensity < 0.0001) {
        camera.rotation.x -= shakeOffsetX;
        camera.rotation.z -= shakeOffsetZ;
        shakeOffsetX = 0;
        shakeOffsetZ = 0;
        shakeIntensity = 0;
      }
    }

    // --- attack vignette ---
    if (vignetteIntensity > 0.001) {
      vignetteIntensity = Math.max(0, vignetteIntensity - delta * 3.5);
      if (attackVignetteEl) attackVignetteEl.style.opacity = vignetteIntensity.toFixed(3);
    } else if (attackVignetteEl && vignetteIntensity <= 0.001) {
      attackVignetteEl.style.opacity = '0';
      vignetteIntensity = 0;
    }

    // --- FOV zoom punch ---
    if (Math.abs(fovPunch) > 0.05) {
      camera.fov = baseFov + fovPunch;
      camera.updateProjectionMatrix();
      fovPunch *= Math.max(0, 1 - fovPunchDecay * delta);
      if (Math.abs(fovPunch) < 0.05) {
        fovPunch = 0;
        camera.fov = baseFov;
        camera.updateProjectionMatrix();
      }
    }
  }

  function getHitStopScale() {
    return hitStopTimer > 0 ? 0 : 1;
  }

  function triggerDamageFovPunch() {
    fovPunch = -5.0;
    fovPunchDecay = 18;
  }

  return {
    tryAttackMob,
    tryShootBow,
    tryScoopFluidWithBucket,
    tryPourFluidFromBucket,
    updateShake,
    getHitStopScale,
    triggerDamageFovPunch
  };
}
